import { useState, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { differenceInDays, parseISO } from 'date-fns';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Header from './Header';
import Sidebar from './Sidebar';
import CommandPalette from '../common/CommandPalette';
import ErrorBoundary from '../common/ErrorBoundary';
import useCommandStore from '../../store/commandStore';
import useAuthStore from '../../store/authStore';
import { getSubscriptionStatus } from '../../services/subscriptionService';

function TrialBanner({ status }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  // No end date means an open-ended / internal trial — nothing to warn about
  if (!status.trialEndsAt) return null;

  // Only show when trialing (not yet subscribed)
  if (status.status !== 'trialing') return null;

  const daysLeft = Math.max(0, differenceInDays(parseISO(status.trialEndsAt), new Date()));
  const isExpired = !status.isSubscribed;

  const bgClass = isExpired
    ? 'bg-red-600 dark:bg-red-700'
    : daysLeft <= 3
    ? 'bg-yellow-500 dark:bg-yellow-600'
    : 'bg-primary-600 dark:bg-primary-700';

  return (
    <div className={`${bgClass} text-white text-sm py-2 px-4 flex items-center justify-between gap-3`}>
      <div className="flex items-center gap-2 flex-1 justify-center">
        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
        {isExpired ? (
          <span>
            Your free trial has expired.{' '}
            <Link to="/settings?tab=billing" className="font-semibold underline hover:no-underline">
              Subscribe now
            </Link>{' '}
            to keep access.
          </span>
        ) : (
          <span>
            {daysLeft === 0 ? 'Trial expires today' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left in your free trial`}.{' '}
            <Link to="/settings?tab=billing" className="font-semibold underline hover:no-underline">
              Subscribe now
            </Link>{' '}
            — $49/month.
          </span>
        )}
      </div>
      <button onClick={() => setDismissed(true)} className="flex-shrink-0 opacity-80 hover:opacity-100">
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { setOpen: openCommand } = useCommandStore();

  const { user } = useAuthStore();

  const { data: subStatus } = useQuery({
    queryKey: ['subscriptionStatus'],
    queryFn: getSubscriptionStatus,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: !user?.isPlatformAdmin,
  });

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openCommand(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [openCommand]);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex flex-col">
      {subStatus && <TrialBanner status={subStatus} />}
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <div className="flex flex-1 min-h-0">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 p-4 md:p-8 min-w-0 transition-all duration-200">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}
